'use client';

import { c as _c } from "react/compiler-runtime";
import { AlertTriangleIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { motion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { globalStore } from '@/app/globalStore';
import { IconClose } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { cleanStackTrace } from '@/lib/utils/cleanStackTrace';
interface ErrorOverlayProps {
  /** Dialog title displayed in the header */
  title?: string;
  /** Single error message (for backwards compatibility) */
  error?: string;
  /** Multiple error messages */
  errors?: string[];
  /** Callback when the overlay is dismissed */
  onDismiss: () => void;
}

/**
 * Highlights lines containing "error" (case-insensitive) by making them bold.
 * Returns an array of React elements with appropriate styling.
 */
/**
 * First non-empty line after stack normalisation, for sidebar labels and tooltips.
 */
function getErrorListSummary(rawError: string): {
  label: string;
  tooltip: string;
} {
  const normalised = cleanStackTrace(rawError).trim();
  const firstLine = normalised.split(/\r?\n/).map(line => line.trim()).find(line => line.length > 0) ?? '';
  if (firstLine.length > 0) {
    return {
      label: firstLine,
      tooltip: firstLine
    };
  }
  const fallback = normalised.length > 0 ? normalised : 'Error';
  return {
    label: fallback,
    tooltip: fallback
  };
}
function renderHighlightedErrorText(text: string): React.ReactNode[] {
  return text.split('\n').map((line, index) => {
    const containsError = line.toLowerCase().includes('error');
    return <span key={index}>
        <span className={cn(containsError && 'font-semibold text-rose-300/95')}>
          {line}
        </span>
        {index < text.split('\n').length - 1 && '\n'}
      </span>;
  });
}
export const ErrorOverlay = t0 => {
  const $ = _c(49);
  const {
    title: t1,
    error,
    errors: errorsProp,
    onDismiss
  } = t0;
  const title = t1 === undefined ? "Error" : t1;
  let t2;
  bb0: {
    if (errorsProp?.length) {
      t2 = errorsProp;
      break bb0;
    }
    if (error) {
      let t3;
      if ($[0] !== error) {
        t3 = [error];
        $[0] = error;
        $[1] = t3;
      } else {
        t3 = $[1];
      }
      t2 = t3;
      break bb0;
    }
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = [];
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    t2 = t3;
  }
  const errors = t2;
  let t3;
  if ($[3] !== errors) {
    t3 = errors.join("\n\n---\n\n");
    $[3] = errors;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const allErrorsText = t3;
  let t4;
  if ($[5] !== errors) {
    t4 = errors.map(_temp);
    $[5] = errors;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const errorListSummaries = t4;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const overlayRootRef = useRef(null);
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      overlayRootRef.current?.focus();
    };
    t6 = [];
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setSelectedIndex(0);
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== errors.length) {
    t8 = [errors.length];
    $[10] = errors.length;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  useEffect(t7, t8);
  let t9;
  if ($[12] !== onDismiss) {
    t9 = e => {
      if (e.key === "Escape") {
        onDismiss();
      }
    };
    $[12] = onDismiss;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const handleKeyDown = t9;
  const selectedError = errors[selectedIndex] ?? "";
  const hasMultipleErrors = errors.length > 1;
  if (errors.length === 0) {
    return null;
  }
  let t10;
  let t11;
  let t12;
  let t13;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      opacity: 0
    };
    t11 = {
      opacity: 1
    };
    t12 = {
      opacity: 0
    };
    t13 = {
      duration: 0.25
    };
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
    $[17] = t13;
  } else {
    t10 = $[14];
    t11 = $[15];
    t12 = $[16];
    t13 = $[17];
  }
  let t14;
  let t15;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      opacity: 0,
      scale: 0.985,
      y: 8
    };
    t15 = {
      opacity: 1,
      scale: 1,
      y: 0
    };
    $[18] = t14;
    $[19] = t15;
  } else {
    t14 = $[18];
    t15 = $[19];
  }
  let t16;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      duration: 0.28,
      ease: [0.16, 1, 0.3, 1]
    };
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div aria-hidden={true} className="pointer-events-none absolute -top-28 -right-20 h-56 w-56 rounded-full bg-rose-500/12 blur-3xl" />;
    t18 = <div aria-hidden={true} className="pointer-events-none absolute -bottom-36 -left-24 h-64 w-64 rounded-full bg-red-600/10 blur-3xl" />;
    t19 = <div aria-hidden={true} className="pointer-events-none absolute inset-y-8 left-0 w-px bg-linear-to-b from-rose-400/25 via-red-500/55 to-red-950/30" />;
    t20 = <div aria-hidden={true} className="pointer-events-none absolute top-0 right-0 left-0 z-10 h-0.5 bg-linear-to-br from-rose-400/70 via-red-600/95 to-red-950/50" />;
    t21 = <div aria-hidden={true} className="pointer-events-none absolute right-0 bottom-0 left-0 z-10 h-0.5 bg-linear-to-br from-rose-400/70 via-red-600/95 to-red-950/50" />;
    $[21] = t17;
    $[22] = t18;
    $[23] = t19;
    $[24] = t20;
    $[25] = t21;
  } else {
    t17 = $[21];
    t18 = $[22];
    t19 = $[23];
    t20 = $[24];
    t21 = $[25];
  }
  let t22;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="relative shrink-0 rounded-lg border border-rose-400/30 bg-linear-to-br from-red-500/12 to-rose-500/8 p-2.5 shadow-[0_0_20px_-8px_oklch(0.65_0.2_25/0.35)]"><AlertTriangleIcon className="size-5 text-rose-400" /></div>;
    $[26] = t22;
  } else {
    t22 = $[26];
  }
  let t23;
  if ($[27] !== errors.length || $[28] !== hasMultipleErrors) {
    t23 = hasMultipleErrors && <span className="ml-2 bg-linear-to-r from-rose-200 to-red-300/90 bg-clip-text font-normal text-transparent">({errors.length} errors)</span>;
    $[27] = errors.length;
    $[28] = hasMultipleErrors;
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  let t24;
  if ($[30] !== t23 || $[31] !== title) {
    t24 = <div className="relative flex shrink-0 items-center gap-4 px-5 pt-6 pb-4 pr-14">{t22}<div className="min-w-0 flex-1"><h3 className="text-lg font-semibold tracking-tight text-white">{title}{t23}</h3></div></div>;
    $[30] = t23;
    $[31] = title;
    $[32] = t24;
  } else {
    t24 = $[32];
  }
  let t25;
  if ($[33] !== allErrorsText || $[34] !== errorListSummaries || $[35] !== hasMultipleErrors || $[36] !== selectedError || $[37] !== selectedIndex) {
    t25 = <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden px-5 pb-5">{hasMultipleErrors ? <div className="flex min-h-0 flex-1 gap-0"><div className="flex min-h-0 w-80 shrink-0 flex-col border-r border-white/6 pr-2"><div className="pl-2 mb-2 flex shrink-0 justify-start"><CopyButton value={selectedError} label={`Copy Error ${selectedIndex + 1}`} variant="transparent" toast="Error copied to clipboard" /></div><ScrollArea className="min-h-0 flex-1 basis-0"><div className="py-1 pr-1">{errorListSummaries.map((t26, index) => {
                const {
                  label,
                  tooltip
                } = t26;
                const isSelected = selectedIndex === index;
                return <button key={index} type="button" title={tooltip} aria-label={`Error ${index + 1}: ${tooltip}`} onClick={() => setSelectedIndex(index)} className={cn("group flex w-full min-w-0 items-start gap-2 select-none rounded-r-lg border-l-2 border-transparent py-2 pr-2 pl-2.5 text-left font-mono text-xs transition-all duration-200", isSelected ? "border-l-rose-400 bg-linear-to-r from-rose-500/15 via-red-500/12 to-transparent text-rose-100 shadow-[inset_0_0_20px_-12px_oklch(0.62_0.22_25/0.22)]" : "text-zinc-500 hover:bg-white/4 hover:text-zinc-200")}><span className={cn("mt-px flex h-5 min-h-5 min-w-5 shrink-0 items-center justify-center rounded-md px-1.5 text-xs font-medium tabular-nums transition-colors", isSelected ? "bg-rose-500/40 text-rose-50 ring-1 ring-rose-300/45 shadow-[0_0_14px_-5px_oklch(0.62_0.22_25/0.55)]" : "bg-zinc-800/95 text-zinc-400 ring-1 ring-white/12 group-hover:bg-zinc-700/95 group-hover:text-zinc-200 group-hover:ring-white/18")} aria-hidden={true}>{index + 1}</span><span className="min-w-0 flex-1 pt-px leading-snug line-clamp-2 break-words text-inherit">{label}</span></button>;
              })}</div></ScrollArea></div><div className="flex min-h-0 min-w-0 flex-1 flex-col pl-4"><div className="mb-2 flex w-full shrink-0 justify-end"><CopyButton value={allErrorsText} label="Copy All Errors" variant="transparent" toast="All errors copied to clipboard" /></div><ScrollArea className="min-h-0 flex-1 basis-0"><div className="selectable-content rounded-lg border border-red-500/20 bg-black/45 p-3 font-mono text-sm whitespace-pre-wrap text-zinc-300 shadow-inner ring-1 ring-rose-500/10">{selectedError && renderHighlightedErrorText(selectedError)}</div></ScrollArea></div></div> : <div className="flex min-h-0 flex-1 flex-col overflow-hidden"><div className="mb-2 flex w-full shrink-0 justify-end"><CopyButton value={selectedError} label="Copy Error" variant="transparent" toast="Error copied to clipboard" /></div><ScrollArea className="min-h-0 flex-1 basis-0"><div className="selectable-content rounded-lg border border-red-500/20 bg-black/45 p-3 font-mono text-sm whitespace-pre-wrap text-zinc-300 shadow-inner ring-1 ring-rose-500/10">{selectedError && renderHighlightedErrorText(selectedError)}</div></ScrollArea></div>}</div>;
    $[33] = allErrorsText;
    $[34] = errorListSummaries;
    $[35] = hasMultipleErrors;
    $[36] = selectedError;
    $[37] = selectedIndex;
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  let t26;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <IconClose className="size-3.5" />;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  let t27;
  if ($[40] !== onDismiss) {
    t27 = <Button variant="transparent" size="icon" type="button" onClick={onDismiss} className="absolute top-3 right-3 p-1.5 text-zinc-500 transition-colors hover:bg-white/5 hover:text-zinc-100" aria-label="Dismiss error">{t26}</Button>;
    $[40] = onDismiss;
    $[41] = t27;
  } else {
    t27 = $[41];
  }
  let t28;
  if ($[42] !== t24 || $[43] !== t25 || $[44] !== t27) {
    t28 = <motion.div className="flex h-[min(max(24rem,min(64vh,calc(100vh-var(--titlebar-height)-2rem))),calc(100vh-var(--titlebar-height)-2rem))] min-h-0 w-full min-w-[800px] max-w-[1400px] flex-col overflow-hidden" initial={t14} animate={t15} transition={t16}><div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-linear-to-br from-rose-400/45 via-red-500/35 to-red-950/50 p-px shadow-xl shadow-red-950/20"><div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-[calc(0.75rem-1px)] bg-zinc-950/94 ring-1 ring-white/5 backdrop-blur-xl">{t17}{t18}{t19}{t20}{t21}{t24}{t25}{t27}</div></div></motion.div>;
    $[42] = t24;
    $[43] = t25;
    $[44] = t27;
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  let t29;
  if ($[46] !== handleKeyDown || $[47] !== t28) {
    t29 = <motion.div initial={t10} animate={t11} exit={t12} transition={t13} className="fixed top-(--titlebar-height) left-0 right-0 bottom-0 z-99999 flex items-center justify-center bg-black/65 p-4 backdrop-blur-md" onKeyDown={handleKeyDown} tabIndex={0} ref={overlayRootRef}>{t28}</motion.div>;
    $[46] = handleKeyDown;
    $[47] = t28;
    $[48] = t29;
  } else {
    t29 = $[48];
  }
  return t29;
};
ErrorOverlay.displayName = 'ErrorOverlay';
export const GlobalBuildErrorOverlay = observer(() => {
  const showBuildErrors = globalStore.showBuildErrorOverlay && globalStore.hasBuildErrors;
  const showPlayModeErrors = globalStore.showPlayModeErrorOverlay && globalStore.hasAnyPlayModeErrors;
  if (!showBuildErrors && !showPlayModeErrors) return null;
  const errors = [...(showBuildErrors ? globalStore.buildErrors.slice() : []), ...(showPlayModeErrors ? globalStore.allPlayModeErrors : [])];
  const title = showBuildErrors && showPlayModeErrors ? 'Errors' : showBuildErrors ? 'Build Failed' : 'Play Mode Errors';
  return <ErrorOverlay title={title} errors={errors} onDismiss={() => {
    globalStore.setShowBuildErrorOverlay(false);
    globalStore.setShowPlayModeErrorOverlay(false);
  }} />;
});
GlobalBuildErrorOverlay.displayName = 'GlobalBuildErrorOverlay';
function _temp(raw) {
  return getErrorListSummary(raw);
}